import { memo, type MouseEvent } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Cards,
  Footer,
  Header,
  MobileChildModules,
  ModuleIconWrapper,
  TitleContent,
  TitleText,
  TitleWrapper,
  Wrapper,
  moduleHeaderBadgeSx,
} from './ModuleWidget.styles';
import { getIcon } from 'utils/common';
import { isFunction } from 'radash';
import { IconWrapper } from '../InfoWidgets/SimpleWidgetCard.styles';
import type { CardProps } from './Card';
import { Card } from './Card';
import { ChildModulesWrapper } from './ChildModulesWrapper';
import { mapAgendasToCardItems } from './moduleWidgetUtils';
import type { Module } from 'types/modules';
import { IconButton } from 'components/mui/common/IconButton';
import { Star, StarBorder } from '@mui/icons-material';
import { CustomBadge } from 'components/mui/common/CustomBadge';
import { ModuleTitleWithTooltip } from './components/ModuleTitleWithTooltip';
import { toTestIdSegment } from 'utils/testId';

interface ModuleWidgetProps {
  title: string;
  moduleId: string;
  favoriteId?: string | null;
  items: CardProps[];
  onShowAllClick?: (moduleId: string, parentModuleId?: string) => void;
  onDetailClick?: (moduleId: string, agendaSourceType: string, agendaId: string, parentModuleId?: string) => void;
  onAddClick?: (moduleId: string, agendaSourceType: string, agendaId: string, parentModuleId?: string) => void;
  onFavoriteClick?: (moduleId: string, isFavorite: boolean) => void;
  showFavorite?: boolean;
  isFavorite?: boolean;
  icon?: string;
  mock?: boolean;
  childModules?: Omit<Module, 'childs'>[];
  parentModuleId?: string;
  overview?: boolean;
  unreadNotificationsCount?: number;
  isPlainIcon?: boolean;
  maxSubordinates?: number;
  isMobileLayout?: boolean;
}

export const ModuleWidget = memo(
  ({
    title,
    moduleId,
    favoriteId,
    items,
    onShowAllClick,
    onDetailClick,
    onFavoriteClick,
    onAddClick,
    showFavorite,
    isFavorite,
    icon,
    mock,
    childModules,
    parentModuleId,
    overview,
    unreadNotificationsCount,
    isPlainIcon = false,
    maxSubordinates,
    isMobileLayout = false,
  }: ModuleWidgetProps) => {
    const { t } = useTranslation();
    const Icon = icon ? getIcon(icon) : null;
    const IconContainer = isPlainIcon ? ModuleIconWrapper : IconWrapper;
    const hasSubordinates = items.length > 0 || (childModules?.length ?? 0) > 0;

    const showAllClickHandler = () => {
      if (isFunction(onShowAllClick)) {
        onShowAllClick(moduleId, parentModuleId);
      }
    };

    const detailClickHandler = (agendaSourceType: string, agendaId: string) => {
      if (isFunction(onDetailClick)) {
        onDetailClick(moduleId, agendaSourceType, agendaId, parentModuleId);
      }
    };

    const addClickHandler = (agendaSourceType: string, agendaId: string) => {
      if (isFunction(onAddClick)) {
        onAddClick(moduleId, agendaSourceType, agendaId, parentModuleId);
      }
    };

    const favoriteClickHandler = (event?: MouseEvent) => {
      event?.stopPropagation();

      if (isFunction(onFavoriteClick)) {
        onFavoriteClick(favoriteId || moduleId, isFavorite ?? false);
      }
    };

    const cardClickHandler = () => {
      if (!isMobileLayout || mock) return;
      showAllClickHandler();
    };

    const favoriteButton = showFavorite && (isFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />);

    const moduleIcon = Icon ? <Icon className="text-primary" fontSize="medium" /> : null;
    const testId = `user-dashboard-module-widget-${toTestIdSegment(moduleId)}`;

    return (
      <Wrapper isMobileLayout={isMobileLayout} hasSubordinates={hasSubordinates} data-testid={testId}>
        {isMobileLayout ? (
          <>
            <Header isMobileLayout hasSubordinates={hasSubordinates} onClick={cardClickHandler} sx={{ cursor: mock ? 'default' : 'pointer' }}>
              <IconContainer>{moduleIcon}</IconContainer>
              <TitleWrapper>
                <TitleContent>
                  <TitleText>
                    <ModuleTitleWithTooltip title={title} />
                  </TitleText>
                  {!!unreadNotificationsCount && (
                    <CustomBadge data-testid={`${testId}-badge`} sx={moduleHeaderBadgeSx}>
                      {unreadNotificationsCount}
                    </CustomBadge>
                  )}
                </TitleContent>
              </TitleWrapper>
              {showFavorite && (
                <IconButton size={20} disabled={mock} onClick={favoriteClickHandler}>
                  {favoriteButton}
                </IconButton>
              )}
            </Header>
            {items.length > 0 && (
              <Cards isMobileLayout onClick={(event) => event.stopPropagation()}>
                {items.map((item, index) => (
                  <Card
                    mock={mock}
                    key={index}
                    {...item}
                    overview={overview}
                    isPlainIcon={isPlainIcon}
                    onClick={detailClickHandler}
                    onAddClick={addClickHandler}
                  />
                ))}
              </Cards>
            )}
            {childModules && childModules.length > 0 && (
              <MobileChildModules onClick={(event) => event.stopPropagation()}>
                {childModules.map((childModule) => (
                  <ModuleWidget
                    key={childModule.moduleId}
                    title={childModule.name}
                    moduleId={childModule.moduleId}
                    items={mapAgendasToCardItems(childModule.agendas)}
                    onShowAllClick={onShowAllClick}
                    onDetailClick={onDetailClick}
                    onAddClick={onAddClick}
                    onFavoriteClick={onFavoriteClick}
                    showFavorite={showFavorite}
                    isFavorite={childModule.isPinned}
                    icon={childModule.icon}
                    mock={mock}
                    parentModuleId={moduleId}
                    unreadNotificationsCount={childModule.unreadNotificationsCount}
                    isPlainIcon={isPlainIcon}
                    isMobileLayout
                  />
                ))}
              </MobileChildModules>
            )}
          </>
        ) : (
          <>
            <div>
              <Header isMobileLayout={false} hasSubordinates={hasSubordinates}>
                <IconContainer>{moduleIcon}</IconContainer>
                <TitleWrapper>
                  <TitleContent>
                    <TitleText>
                      <ModuleTitleWithTooltip title={title} />
                    </TitleText>
                    {!!unreadNotificationsCount && (
                      <CustomBadge data-testid={`${testId}-badge`} sx={moduleHeaderBadgeSx}>
                        {unreadNotificationsCount}
                      </CustomBadge>
                    )}
                  </TitleContent>
                </TitleWrapper>
              </Header>
              {items.length > 0 && (
                <Cards>
                  {items.map((item, index) => (
                    <Card
                      mock={mock}
                      key={index}
                      {...item}
                      overview={overview}
                      isPlainIcon={isPlainIcon}
                      onClick={detailClickHandler}
                      onAddClick={addClickHandler}
                    />
                  ))}
                </Cards>
              )}
              {childModules && childModules.length > 0 && (
                <ChildModulesWrapper
                  childModules={childModules}
                  onShowAllClick={onShowAllClick}
                  onDetailClick={onDetailClick}
                  onAddClick={onAddClick}
                  onFavoriteClick={onFavoriteClick}
                  showFavorite={showFavorite}
                  mock={mock}
                  parentModuleId={moduleId}
                  isPlainIcon={isPlainIcon}
                  maxSubordinates={maxSubordinates}
                />
              )}
            </div>
            <Footer>
              {mock ? null : (
                <Typography sx={{ p: '0 8px 8px' }} onClick={showAllClickHandler} data-testid={`${testId}-show-all`}>
                  {t('user.modules.showAll')}
                </Typography>
              )}
              {showFavorite && (
                <IconButton size={20} disabled={mock} onClick={() => favoriteClickHandler()}>
                  {favoriteButton}
                </IconButton>
              )}
            </Footer>
          </>
        )}
      </Wrapper>
    );
  }
);
